import { useNavigation, Authenticated } from "@refinedev/core";
import React from "react";
import { Form, Flex, FormInstance } from "antd";
import styled from "styled-components";
import { BrandKit } from "../types";
import { Create } from "@refinedev/antd";
import PageHeader from "components/PageHeader";
import { useAntTheme } from "hooks/useAntTheme";
import { BigInput } from "components/BigInput/BigInput/";
import { useBrandKitEdit } from "./hooks/useBrandkitEdit";
import { BackToButton } from "pages/components/BackToButton";
import { ActionButton } from "pages/components/ActionButton";
import { useTranslate } from "#src/hooks";

export const BrandKitCreate: React.FC = () => {
  const t = useTranslate();
  const { handleCreateBrandKit, isCreatingBrandKit } = useBrandKitEdit();
  const { theme, isMobile } = useAntTheme();
  const [form] = Form.useForm();
  const { list } = useNavigation();

  const onFinishFailed = ({ errorFields }: any) => {
    if (errorFields.length) {
      const firstErrorField = errorFields[0];
      const fieldElement = document.querySelector(
        `#${firstErrorField.name[0]}`
      );
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  return (
    <Authenticated key="brandkit_create">
      <StyledWrapper>
        <Create
          breadcrumb={isMobile ? false : undefined}
          footerButtons={
            <Flex gap={52} align="center" justify="center">
              {!isMobile && (
                <ActionButton
                  onClick={() => list("media_brandkits")}
                  disabled={isCreatingBrandKit}
                  iconPosition="start"
                >
                  {t("buttons.back")}
                </ActionButton>
              )}
              <ActionButton
                onClick={form.submit}
                disabled={isCreatingBrandKit}
                iconPosition="end"
                type="primary"
              >
                {t("buttons.next")}
              </ActionButton>
            </Flex>
          }
          goBack={
            <BackToButton resource={t("media.brandkit.index.brandKits")} />
          }
          title={false}
        >
          <BaseBrandkitCreate
            form={form}
            handleSave={() =>
              handleCreateBrandKit({
                name: form.getFieldsValue().name,
                primaryColor: theme.colorBorder,
              })
            }
            onFinishFailed={onFinishFailed}
          />
        </Create>
      </StyledWrapper>
    </Authenticated>
  );
};

export const StyledWrapper = styled.div`
  width: 100%;
  min-height: 60vh;
  height: fit-content;
  .ant-card {
    background: none;
    box-shadow: none;
    padding: 0;
  }
  .ant-card-actions {
    background: none;
    border: 0;
    > li > span > .ant-space {
      display: flex;
      float: none !important;
      width: 100%;
      > .ant-space-item {
        width: 100%;
      }
    }
  }
`;

interface BaseBrandkitCreateProps {
  form: FormInstance;
  handleSave: () => Promise<BrandKit | undefined>;
  onSuccess?: (brandKit: BrandKit) => void;
  onFinishFailed?: ({ errorFields }: any) => void;
}

export const BaseBrandkitCreate = ({
  form,
  handleSave,
  onSuccess,
  onFinishFailed,
}: BaseBrandkitCreateProps) => {
  const t = useTranslate();
  const { isMobile } = useAntTheme();
  const onFinish = async () => {
    const result = await handleSave();
    if (result && onSuccess) {
      onSuccess(result);
    }
  };

  return (
    <Flex
      vertical
      gap={50}
      style={{ width: "100%", height: "100%" }}
      justify="center"
    >
      <PageHeader
        title={t("media.brandkit.create.createBrandkit.title")}
        subtitle={t("media.brandkit.create.createBrandkit.subtitle")}
        dot={false}
      />
      <Form
        requiredMark
        form={form}
        onFinish={() => {
          onFinish().catch((error) =>
            console.error("Error creating brandkit", error)
          );
        }}
        onFinishFailed={onFinishFailed}
        onKeyDown={(e) => {
          //to not submit form on Enter, because it prevents call saveCallback function on Modal OK
          if (e.key === "Enter") {
            e.preventDefault();
            return false;
          }
        }}
        layout="vertical"
        style={{
          width: isMobile ? "100%" : "40%",
          margin: "0 auto",
        }}
      >
        <Form.Item
          name="name"
          label={false}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Flex justify="center">
            <BigInput
              placeholder={t(
                "media.brandkit.create.createBrandkit.placeholder"
              )}
              style={{ opacity: 0.5, width: "100%" }}
            />
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
};
